import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { LineChart, BarChart } from '@mui/x-charts';
import {
  decorateChartSeries,
  defaultVisibleMetricIds,
} from '../../constants/chartSeriesMetrics';
import {
  areWholeNumberValues,
  formatChartDateLabel,
  formatChartTickValue,
  formatDurationSeconds,
  resolveBarChartWidth,
} from '../../utils/chartFormatUtils';
import useAnalyticsChartPalette from '../../hooks/useAnalyticsChartPalette';

const SENT_ALIASES = ['messages_sent', 'sent', 'sent_count', 'message_sent'];
const DELIVERED_ALIASES = ['messages_delivered', 'delivered', 'delivered_count', 'message_delivered'];
const READ_ALIASES = ['messages_read', 'read', 'read_count', 'message_read'];
const FAILED_ALIASES = ['messages_failed', 'failed', 'failed_count', 'message_failed'];
const CTA_ALIASES = ['cta_clicks_total', 'cta_clicks', 'button_clicks', 'cta_count'];

const RED_COLOR_REGEX = /^(#e53935|#d32f2f|#f44336|#ef4444|#dc2626|#b71c1c|#c62828|red)$/i;

function preventRedBarColor(color, fallback = '#64748b') {
  if (!color) return color;
  const normalized = String(color).trim();
  if (RED_COLOR_REGEX.test(normalized)) {
    return fallback;
  }
  return color;
}

function MetricToggles({ series, selectedIds, onToggle }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      sx={{ mb: 1.5 }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 0.6, lineHeight: 1, mr: 0.5 }}
      >
        Metrics
      </Typography>
      {series.map((metric) => {
        const selected = selectedIds.includes(metric.id);
        const safeColor = preventRedBarColor(metric.color);
        return (
          <Chip
            key={metric.id}
            label={metric.label}
            size="small"
            clickable
            onClick={() => onToggle(metric.id)}
            sx={{
              height: 28,
              fontWeight: 600,
              fontSize: 12,
              border: '1px solid',
              borderColor: safeColor,
              color: selected ? '#fff' : safeColor,
              backgroundColor: selected ? safeColor : 'transparent',
              '&:hover': {
                backgroundColor: selected ? safeColor : `${safeColor}18`,
              },
            }}
          />
        );
      })}
    </Stack>
  );
}

export default function LineBarChart({
  result,
  kind = 'line',
  colors = [],
  height = 300,
  xAxisLabel = '',
  yAxisLabel = '',
  valueFormat = 'number',
  compact = false,
  displayMode: externalDisplayMode,
  isRecords = false,
}) {
  const { colors: themeColors, primary, axis } = useAnalyticsChartPalette(colors);
  const [internalDisplayMode, setInternalDisplayMode] = useState('count');
  const displayMode = externalDisplayMode || internalDisplayMode;
  const activeKind = kind;

  const parsed = useMemo(() => {
    if (!result) return { xLabels: [], values: [], series: [] };
    if (result.categories && result.series?.length > 1) {
      const series = decorateChartSeries(result.series, themeColors);
      return {
        xLabels: result.categories.map(formatChartDateLabel),
        values: series.flatMap((item) => item.data),
        series,
      };
    }
    if (result.categories && result.series) {
      const data = result.series[0]?.data || [];
      return {
        xLabels: result.categories.map(formatChartDateLabel),
        values: data,
        series: [{ id: 'primary', data, label: yAxisLabel || 'Value' }],
      };
    }
    const points = result.series?.[0]?.data || [];
    return {
      xLabels: points.map((p) => formatChartDateLabel(p.x)),
      values: points.map((p) => p.y),
      series: [{ id: 'primary', data: points.map((p) => p.y), label: yAxisLabel || 'Value' }],
    };
  }, [result, yAxisLabel, themeColors]);

  const { xLabels, values, series } = parsed;
  const multi = series.length > 1;
  const seriesKey = series.map((item) => item.id).join('|');
  const [selectedIds, setSelectedIds] = useState(() => defaultVisibleMetricIds(series));

  useEffect(() => {
    setSelectedIds(defaultVisibleMetricIds(series));
  }, [seriesKey]);

  // Compute percentage data when in percentage mode or for contextual tooltips
  const percentageRates = useMemo(() => {
    if (!multi || valueFormat === 'duration') return null;

    const sentSeries = series.find((s) => SENT_ALIASES.includes(s.id));
    const deliveredSeries = series.find((s) => DELIVERED_ALIASES.includes(s.id));

    return series.map((s) => {
      const isSent = SENT_ALIASES.includes(s.id);
      const isDelivered = DELIVERED_ALIASES.includes(s.id);
      const isRead = READ_ALIASES.includes(s.id);
      const isFailed = FAILED_ALIASES.includes(s.id);
      const isCta = CTA_ALIASES.includes(s.id);

      const pctData = (s.data || []).map((val, idx) => {
        const num = Number(val) || 0;
        const sentVal = Number(sentSeries?.data?.[idx]) || 0;
        const deliveredVal = Number(deliveredSeries?.data?.[idx]) || 0;

        if (isSent) {
          return sentVal > 0 ? 100 : 0;
        }
        if (isDelivered) {
          return sentVal > 0 ? Number(((num / sentVal) * 100).toFixed(1)) : 0;
        }
        if (isRead) {
          const base = deliveredVal > 0 ? deliveredVal : sentVal;
          return base > 0 ? Number(((num / base) * 100).toFixed(1)) : 0;
        }
        if (isFailed) {
          return sentVal > 0 ? Number(((num / sentVal) * 100).toFixed(1)) : 0;
        }
        if (isCta) {
          const base = deliveredVal > 0 ? deliveredVal : sentVal;
          return base > 0 ? Number(((num / base) * 100).toFixed(1)) : 0;
        }

        const colTotal = series.reduce((sum, item) => sum + (Number(item.data?.[idx]) || 0), 0);
        return colTotal > 0 ? Number(((num / colTotal) * 100).toFixed(1)) : 0;
      });

      return {
        id: s.id,
        data: pctData,
      };
    });
  }, [multi, series, valueFormat]);

  const isPercentageMode = displayMode === 'percentage';
  const effectiveSeries = isPercentageMode && percentageRates
    ? series.map((item) => {
        const pctItem = percentageRates.find((p) => p.id === item.id);
        return {
          ...item,
          data: pctItem?.data || item.data,
        };
      })
    : series;

  const visibleSeries = multi
    ? effectiveSeries.filter((item) => selectedIds.includes(item.id))
    : effectiveSeries;

  // Header totals computation
  const seriesTotals = useMemo(() => {
    if (!multi) return [];

    if (isPercentageMode && percentageRates) {
      const sentSeries = series.find((s) => SENT_ALIASES.includes(s.id));
      const deliveredSeries = series.find((s) => DELIVERED_ALIASES.includes(s.id));
      const totSent = (sentSeries?.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0);
      const totDelivered = (deliveredSeries?.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0);

      return visibleSeries.map((item) => {
        const rawSeries = series.find((s) => s.id === item.id);
        const totVal = (rawSeries?.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0);

        let rateStr = '0%';
        if (SENT_ALIASES.includes(item.id)) {
          rateStr = totSent > 0 ? '100%' : '0%';
        } else if (DELIVERED_ALIASES.includes(item.id)) {
          rateStr = totSent > 0 ? `${((totDelivered / totSent) * 100).toFixed(1)}%` : '0%';
        } else if (READ_ALIASES.includes(item.id)) {
          const base = totDelivered > 0 ? totDelivered : totSent;
          rateStr = base > 0 ? `${((totVal / base) * 100).toFixed(1)}%` : '0%';
        } else if (FAILED_ALIASES.includes(item.id)) {
          rateStr = totSent > 0 ? `${((totVal / totSent) * 100).toFixed(1)}%` : '0%';
        } else if (CTA_ALIASES.includes(item.id)) {
          const base = totDelivered > 0 ? totDelivered : totSent;
          rateStr = base > 0 ? `${((totVal / base) * 100).toFixed(1)}%` : '0%';
        } else {
          const avgPct = (item.data || []).reduce((sum, v) => sum + v, 0) / Math.max(1, item.data.length);
          rateStr = `${avgPct.toFixed(1)}%`;
        }

        return {
          id: item.id,
          label: item.label,
          total: rateStr,
        };
      });
    }

    return visibleSeries.map((item) => ({
      id: item.id,
      label: item.label,
      total: (item.data || []).reduce((sum, value) => sum + (Number(value) || 0), 0).toLocaleString(),
    }));
  }, [isPercentageMode, multi, percentageRates, series, visibleSeries]);

  const palette = themeColors;
  const rawValues = visibleSeries.flatMap((item) => item.data || []);
  const useIntegerTicks = !isPercentageMode && valueFormat !== 'duration' && areWholeNumberValues(rawValues);
  const longestCategoryLength = xLabels.reduce(
    (longest, label) => Math.max(longest, String(label).length),
    0
  );
  const chartWidth = resolveBarChartWidth(
    xLabels.length,
    longestCategoryLength,
    activeKind === 'bar' ? visibleSeries.length : 1
  );
  const maxValue = Math.max(...(rawValues.length ? rawValues : values), 0, 1);
  const chartHeight = height + (compact ? 40 : 0);
  const margin = {
    top: 16,
    right: 36,
    bottom: 72,
    left: valueFormat === 'duration' ? 52 : isPercentageMode ? 48 : 44,
  };

  const xAxis = [{
    scaleType: activeKind === 'bar' ? 'band' : 'point',
    data: xLabels,
    label: undefined,
    tickLabelStyle: { fontSize: 12, textAnchor: 'middle' },
    tickLabelInterval: () => true,
    categoryGapRatio: activeKind === 'bar' ? (multi ? 0.26 : 0.38) : undefined,
    barGapRatio: activeKind === 'bar' ? 0.08 : undefined,
  }];

  const formatTick = (val) => {
    if (isPercentageMode) {
      return `${Math.round(val)}%`;
    }
    if (valueFormat === 'duration') {
      return formatDurationSeconds(val);
    }
    return formatChartTickValue(val);
  };

  const yAxis = [{
    label: undefined,
    width: valueFormat === 'duration' ? 48 : isPercentageMode ? 44 : 36,
    min: 0,
    max: isPercentageMode ? Math.max(100, Math.ceil(maxValue)) : undefined,
    tickNumber: isPercentageMode
      ? 5
      : useIntegerTicks
        ? Math.min(6, Math.max(2, Math.ceil(maxValue) + 1))
        : 5,
    tickMinStep: useIntegerTicks ? 1 : undefined,
    valueFormatter: formatTick,
  }];

  const chartSeries = visibleSeries.map((item, index) => {
    const isRecordChart = isRecords || (!multi && activeKind === 'bar');
    const defaultColor = isRecordChart ? '#008d4d' : (palette[index % palette.length] || primary);
    const rawColor = isRecordChart ? '#008d4d' : (item.color || defaultColor);
    const seriesColor = activeKind === 'bar' ? preventRedBarColor(rawColor) : rawColor;

    const seriesValueFormatter = (value, context) => {
      if (value === null || value === undefined) return '';
      if (isPercentageMode) {
        return `${Number(value).toFixed(1)}%`;
      }
      const formatted = valueFormat === 'duration'
        ? formatDurationSeconds(value)
        : formatChartTickValue(value);

      // In count mode, enrich tooltip with contextual percentage if available
      if (context?.dataIndex != null && percentageRates) {
        const pctItem = percentageRates.find((p) => p.id === item.id);
        const pct = pctItem?.data?.[context.dataIndex];
        if (pct !== undefined && !SENT_ALIASES.includes(item.id)) {
          return `${formatted} (${pct}%)`;
        }
      }
      return formatted;
    };

    return {
      id: item.id,
      data: item.data,
      label: item.label,
      color: seriesColor,
      valueFormatter: seriesValueFormatter,
      curve: 'linear',
      showMark: true,
    };
  });

  const toggleMetric = (id) => {
    setSelectedIds((current) => {
      if (current.includes(id)) {
        if (current.length === 1) return current;
        return current.filter((item) => item !== id);
      }
      return [...current, id];
    });
  };

  // For records reports and single-series bar charts, strictly use the original green color #008d4d
  const singleSeriesBarStyles = useMemo(() => {
    const isRecordChart = isRecords || (!multi && activeKind === 'bar');
    if (!isRecordChart) return {};
    const greenColor = '#008d4d';
    return {
      '& .MuiBarElement-root': { fill: `${greenColor} !important` },
      '& path.MuiBarElement-root': { fill: `${greenColor} !important` },
      '& rect.MuiBarElement-root': { fill: `${greenColor} !important` },
      '& g .MuiBarElement-root': { fill: `${greenColor} !important` },
      '& .MuiLineElement-root': { stroke: `${greenColor} !important` },
      '& .MuiMarkElement-root': { fill: `${greenColor} !important`, stroke: `${greenColor} !important` },
    };
  }, [isRecords, multi, activeKind]);

  if (!xLabels.length) return null;

  const chartProps = {
    height: chartHeight,
    margin,
    xAxis,
    yAxis,
    series: chartSeries,
    colors: palette.map((c) => (activeKind === 'bar' ? preventRedBarColor(c) : c)),
    legend: { hidden: true },
    grid: { horizontal: true },
    sx: {
      '& .MuiChartsAxis-label': { fontSize: 12, fontWeight: 600, fill: axis.text },
      '& .MuiChartsAxis-tickLabel': { fontSize: 12, fill: axis.text },
      '& .MuiChartsGrid-line': { stroke: axis.grid },
      '& .MuiLineElement-root': { strokeWidth: 2.5 },
      '& .MuiMarkElement-root': { strokeWidth: 2 },
      ...singleSeriesBarStyles,
      '& .MuiBarLabel-root': {
        fill: '#ffffff',
        fontSize: 11,
        fontWeight: 700,
        textShadow: '0 1px 2px rgba(0,0,0,0.75)',
        pointerEvents: 'none',
      },
      margin:'1px',
    },
  };

  const chartNode = activeKind === 'bar' ? (
    <BarChart
      {...chartProps}
      borderRadius={6}
      barLabel={(item) => {
        const value = item?.value;
        if (value === undefined || value === null || Number(value) <= 0) return '';
        // In grouped bars, hide label on tiny bars to prevent overlapping and bleeding over the bottom axis line
        if (multi && (Number(value) < maxValue * 0.14 || Number(value) < 2)) {
          return '';
        }
        if (isPercentageMode) {
          return `${Number(value).toFixed(0)}%`;
        }
        return valueFormat === 'duration'
          ? formatDurationSeconds(value)
          : formatChartTickValue(value);
      }}
    />
  ) : (
    <LineChart {...chartProps} />
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {multi ? (
        <MetricToggles
          series={series}
          selectedIds={selectedIds}
          onToggle={toggleMetric}
        />
      ) : null}
      {seriesTotals.length ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 1,
            color: axis.text,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {seriesTotals.map((item) => `${item.label}: ${item.total}`).join(' · ')}
        </Typography>
      ) : null}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Box
          sx={{
            width: chartWidth ? `max(100%, ${chartWidth}px)` : '100%',
            minWidth: chartWidth ? `max(100%, ${chartWidth}px)` : '100%',
            flexShrink: 0,
          }}
        >
          {chartNode}
        </Box>
      </Box>
      {xAxisLabel ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.25,
            textAlign: 'center',
            color: axis.text,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {xAxisLabel}
        </Typography>
      ) : null}
    </Box>
  );
}
